import React from "react";
import { Link } from "react-router-dom";
import EmptyStateCard from "../../../components/shared/EmptyStateCard";
import DetailMetricCard from "../../../components/shared/DetailMetricCard";
import PrimaryActionButton from "../../../components/shared/PrimaryActionButton";
import PageHero from "../../../components/shared/PageHero";
import StatusBadge from "../../../components/shared/StatusBadge";
import StatusPanel from "../../../components/shared/StatusPanel";
import SurfaceCard from "../../../components/shared/SurfaceCard";
import RichiesteService from "../../../services/RichiesteService";
import { formatDateTime } from "../../../utils/date";
import {
  getCustomerOfferEyebrow,
  getCustomerOrderStatusPanel,
  getCustomerSummaryPanel,
  getOrderFulfillmentPolicy,
} from "../../../utils/marketplaceStatus";
import { getRichiestaExpiresAt, isRichiestaExpired } from "../../../utils/richiestaExpiry";
import "../../../styles/screens/marketplace/requests/LeMieRichieste.css";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} EUR`;

const getSortedOffers = (offerte = []) => {
  return [...(Array.isArray(offerte) ? offerte : [])].sort(
    (left, right) => Number(left.prezzoTotale || 0) - Number(right.prezzoTotale || 0)
  );
};

const getBaseTotal = (prodotti = []) => {
  return (Array.isArray(prodotti) ? prodotti : []).reduce(
    (total, prodotto) => total + Number(prodotto.prezzoIndicativo || 0) * Number(prodotto.quantita || 0),
    0
  );
};

const getOfferSavings = (offerta, baseTotal) => {
  const lineSavings = Array.isArray(offerta?.voci)
    ? offerta.voci.reduce((total, voce) => total + Number(voce?.scontoValore || 0), 0)
    : 0;
  const explicitSavings = lineSavings + Number(offerta?.scontoOrdineValore || 0);

  if (explicitSavings > 0) {
    const savings = Number(explicitSavings.toFixed(2));
    const percent = baseTotal > 0 ? Math.max(0, Math.round((savings / baseTotal) * 100)) : 0;
    return { savings, percent };
  }

  const total = Number(offerta?.prezzoTotale || 0);
  const savings = Math.max(0, Number((Number(baseTotal || 0) - total).toFixed(2)));
  const percent = baseTotal > 0 ? Math.max(0, Math.round((savings / baseTotal) * 100)) : 0;

  return { savings, percent };
};

const getOfferLineSavings = (voce, prodotti = []) => {
  const matchedProduct = (Array.isArray(prodotti) ? prodotti : []).find(
    (prodotto) => prodotto?.nome === voce?.descrizione
  );

  if (!matchedProduct) {
    return { savings: 0, percent: 0 };
  }

  const baseLineTotal =
    Number(matchedProduct.prezzoIndicativo || 0) * Number(voce?.quantita || matchedProduct.quantita || 0);
  const offerLineTotal = Number(voce?.prezzo || 0);
  const savings = Math.max(0, Number((baseLineTotal - offerLineTotal).toFixed(2)));
  const percent = baseLineTotal > 0 ? Math.max(0, Math.round((savings / baseLineTotal) * 100)) : 0;

  return { savings, percent };
};

const getRequestDisplayTitle = (richiesta) => {
  const title = (richiesta?.titolo || "").trim();
  if (title && !/^Richiesta farmaci/i.test(title)) {
    return title;
  }

  const prodotti = Array.isArray(richiesta?.prodotti) ? richiesta.prodotti : [];
  if (prodotti.length === 0) {
    return title || "Ordine marketplace";
  }

  const firstName = prodotti[0]?.nome || "Prodotto";
  return prodotti.length === 1
    ? `Ordine ${firstName}`
    : `Ordine ${firstName} +${prodotti.length - 1}`;
};

class DettaglioRichiestaCliente extends React.Component {
  state = {
    richiesta: null,
    isLoading: true,
    isAcceptingOffer: false,
    isStartingCheckout: false,
    error: "",
    acceptError: "",
    acceptSuccess: "",
    paymentError: "",
    paymentNotice: "",
  };

  componentDidMount() {
    this.loadRichiesta();
  }

  getPaymentMessageFromQuery = () => {
    const params = new URLSearchParams(this.props.location.search);
    const paymentStatus = params.get("payment");

    if (paymentStatus === "success") {
      return "Pagamento completato";
    }

    if (paymentStatus === "cancelled") {
      return "Pagamento annullato. Puoi riprovare quando vuoi dalla richiesta.";
    }

    return "";
  };

  loadRichiesta = async () => {
    this.setState({
      richiesta: null,
      isLoading: true,
      isAcceptingOffer: false,
      isStartingCheckout: false,
      error: "",
      acceptError: "",
      acceptSuccess: "",
      paymentError: "",
      paymentNotice: this.getPaymentMessageFromQuery(),
    });

    try {
      const richiesta = await RichiesteService.getById(this.props.match.params.richiestaId);
      this.setState({ richiesta, isLoading: false });
    } catch (error) {
      this.setState({
        richiesta: null,
        isLoading: false,
        error: "Impossibile caricare il dettaglio della richiesta.",
      });
    }
  };

  acceptOffer = async (offerta) => {
    const { richiesta } = this.state;
    if (!richiesta || !offerta || isRichiestaExpired(richiesta)) {
      return;
    }

    const now = new Date().toISOString();
    const selectedOfferId = offerta.id || offerta.sellerId || null;
    const updatedOffers = (richiesta.offerte || []).map((currentOffer) => {
      const currentOfferId = currentOffer.id || currentOffer.sellerId;
      const isTarget = currentOfferId === selectedOfferId;
      return {
        ...currentOffer,
        stato: isTarget ? "SELEZIONATA" : "RIFIUTATA",
        letta: true,
      };
    });

    const updatedTracking = [
      ...(Array.isArray(richiesta.tracking) ? richiesta.tracking : []),
      {
        stato: "IN_ATTESA_PAGAMENTO",
        timestamp: now,
        note: "Offerta selezionata dal cliente in attesa di pagamento.",
      },
    ];

    this.setState({
      isAcceptingOffer: true,
      acceptError: "",
      acceptSuccess: "",
      paymentError: "",
      paymentNotice: "",
    });

    try {
      const updated = await RichiesteService.update(richiesta._id, {
        ...richiesta,
        stato: "IN_ATTESA_PAGAMENTO",
        selectedSellerId: offerta.sellerId || richiesta.selectedSellerId || null,
        offertaAccettataId: selectedOfferId,
        offerte: updatedOffers,
        unreadOffersCount: 0,
        tracking: updatedTracking,
      });

      this.setState({
        richiesta: updated,
        isAcceptingOffer: false,
        acceptSuccess: "Proposta selezionata correttamente. Ora puoi procedere al pagamento.",
      });
    } catch (error) {
      this.setState({
        isAcceptingOffer: false,
        acceptError: "Non siamo riusciti ad accettare l'offerta. Riprova tra poco.",
      });
    }
  };

  startCheckout = async () => {
    const { richiesta } = this.state;
    if (!richiesta || isRichiestaExpired(richiesta)) {
      return;
    }

    this.setState({ isStartingCheckout: true, paymentError: "", paymentNotice: "" });

    try {
      const session = await RichiesteService.createCheckoutSession(richiesta._id);
      if (!session?.url) {
        throw new Error("Missing checkout url");
      }

      window.location.assign(session.url);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      this.setState({
        isStartingCheckout: false,
        paymentError:
          typeof detail === "string" ? detail : "Impossibile avviare il pagamento. Riprova tra poco.",
      });
    }
  };

  renderOfferCard = (offerta, index, baseTotal, acceptedOfferId, paymentState) => {
    const voci = Array.isArray(offerta.voci)
      ? offerta.voci
      : Array.isArray(offerta.prodotti)
      ? offerta.prodotti.map((prodotto) => ({
          descrizione: prodotto.nome,
          quantita: prodotto.quantita,
          prezzo: Number(prodotto.prezzoOfferto || prodotto.prezzoIndicativo || 0) * Number(prodotto.quantita || 1),
        }))
      : [];

    const isBest = index === 0;
    const offerId = offerta.id || offerta.sellerId;
    const isAccepted = offerId === acceptedOfferId || ["SELEZIONATA", "ACCETTATA"].includes(offerta.stato);
    const hasAcceptedOffer = Boolean(acceptedOfferId);
    const isPaymentPending = paymentState === "IN_ATTESA";
    const isPaid = paymentState === "PAGATO" || this.state.richiesta?.stato === "PAGATA";
    const isExpired = isRichiestaExpired(this.state.richiesta);
    const isDisabled = this.state.isAcceptingOffer || hasAcceptedOffer || isPaymentPending || isPaid || isExpired;
    const isLockedByOtherOffer = !isAccepted && (hasAcceptedOffer || isPaymentPending || isPaid);
    const savings = getOfferSavings(offerta, baseTotal);
    const canShowSavings = savings.percent > 0 || savings.savings > 0;
    const offerEyebrow = getCustomerOfferEyebrow({
      isPaid,
      isAccepted,
      isPaymentPending,
      isLockedByOtherOffer,
      isBest,
      index,
    });

    return (
      <SurfaceCard
        key={offerta.id || `${offerta.sellerId || "offer"}_${index}`}
        className={`customer-offer-card ${isBest ? "is-best" : ""} ${isAccepted ? "is-accepted" : ""} ${
          isLockedByOtherOffer ? "is-rejected" : ""
        }`}
      >
        <div className="customer-offer-card__top">
          <div>
            <span className="customer-offer-card__eyebrow">{offerEyebrow}</span>
            <h3>{`Proposta ${index + 1}`}</h3>
            <p>Totale ordine {formatPrice(offerta.prezzoTotale)}</p>
            <div className="customer-offer-card__shipping">
              <i className="fa fa-truck" />
              <span>Spedizione</span>
              <strong>{Number(offerta.costoSpedizione || 0) > 0 ? formatPrice(offerta.costoSpedizione) : "Inclusa"}</strong>
            </div>
          </div>
          <div className="customer-offer-card__total">
            <span>Totale offerta</span>
            <strong>{formatPrice(offerta.prezzoTotale)}</strong>
            {canShowSavings ? (
              <div className="customer-offer-card__discount-badge">
                <i className="fa fa-tags" />
                <span>Sconto</span>
                <b>{savings.percent > 0 ? `-${savings.percent}%` : formatPrice(savings.savings)}</b>
              </div>
            ) : null}
          </div>
        </div>

        <div className="customer-offer-card__meta">
          <span>
            <i className="fa fa-envelope-open-o" />
            Offerta del {formatDateTime(offerta.createdAt || this.state.richiesta?.updatedAt)}
          </span>
          {savings.savings > 0 ? (
            <span>
              <i className="fa fa-percent" />
              Risparmio {formatPrice(savings.savings)}
            </span>
          ) : null}
        </div>

        {voci.length > 0 ? (
          <ul className="customer-offer-card__items">
            {voci.map((voce, voceIndex) => {
              const lineSavings = getOfferLineSavings(voce, this.state.richiesta?.prodotti);
              const showLineSavings = lineSavings.percent > 0 || lineSavings.savings > 0;

              return (
                <li key={`${voce.descrizione}-${voceIndex}`}>
                  <div>
                    <strong>{voce.descrizione}</strong>
                    <span>Quantita {voce.quantita || 0}</span>
                  </div>
                  <div className="customer-offer-card__item-price">
                    <strong>{formatPrice(voce.prezzo)}</strong>
                    {showLineSavings ? (
                      <span className="customer-offer-card__item-discount">
                        <i className="fa fa-tags" />
                        {lineSavings.percent > 0 ? `-${lineSavings.percent}%` : formatPrice(lineSavings.savings)}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="customer-offer-card__actions">
          {isAccepted ? (
            isExpired ? (
              <PrimaryActionButton className="customer-request-card__cta" icon="fa-clock-o" variant="secondary" disabled>
                Richiesta scaduta
              </PrimaryActionButton>
            ) : isPaid ? (
              <PrimaryActionButton className="customer-request-card__cta" icon="fa-check-circle" variant="success" disabled>
                Pagamento completato
              </PrimaryActionButton>
            ) : isPaymentPending ? (
              <PrimaryActionButton className="customer-request-card__cta" icon="fa-circle-o-notch fa-spin" variant="warning" disabled>
                Pagamento in verifica
              </PrimaryActionButton>
            ) : (
              <PrimaryActionButton
                className="customer-request-card__cta"
                icon={this.state.isStartingCheckout ? "fa-circle-o-notch fa-spin" : "fa-credit-card"}
                variant="primary"
                onClick={this.startCheckout}
                disabled={this.state.isStartingCheckout}
              >
                {this.state.isStartingCheckout ? "Reindirizzamento a Stripe..." : "Vai al pagamento"}
              </PrimaryActionButton>
            )
          ) : isLockedByOtherOffer ? (
            <PrimaryActionButton className="customer-request-card__cta" icon="fa-times-circle" variant="secondary" disabled>
              Offerta non selezionata
            </PrimaryActionButton>
          ) : isExpired ? (
            <PrimaryActionButton className="customer-request-card__cta" icon="fa-clock-o" variant="secondary" disabled>
              Richiesta scaduta
            </PrimaryActionButton>
          ) : (
            <PrimaryActionButton
              className="customer-request-card__cta"
              icon={this.state.isAcceptingOffer ? "fa-circle-o-notch fa-spin" : "fa-check-circle"}
              disabled={isDisabled}
              onClick={() => this.acceptOffer(offerta)}
            >
              {this.state.isAcceptingOffer ? "Selezione in corso..." : "Seleziona proposta"}
            </PrimaryActionButton>
          )}
        </div>
      </SurfaceCard>
    );
  };

  render() {
    const {
      richiesta,
      isLoading,
      error,
      acceptError,
      acceptSuccess,
      paymentError,
      paymentNotice,
    } = this.state;

    if (isLoading) {
      return (
        <div className="customer-requests-shell">
          <EmptyStateCard
            icon="fa-circle-o-notch fa-spin"
            eyebrow="Caricamento"
            title="Stiamo preparando il dettaglio della richiesta"
            description="Ancora qualche secondo e troverai qui riepilogo, offerte ricevute e stato del pagamento."
          />
        </div>
      );
    }

    if (error || !richiesta) {
      return (
        <div className="customer-requests-shell">
          <EmptyStateCard
            icon="fa-exclamation-circle"
            eyebrow="Errore"
            title="Dettaglio richiesta non disponibile"
            description={error || "Non siamo riusciti a recuperare questa richiesta."}
            action={
              <Link to={`${process.env.PUBLIC_URL}/richieste`} className="marketplace-primary-link">
                Torna alle richieste
              </Link>
            }
          />
        </div>
      );
    }

    const offerte = getSortedOffers(richiesta.offerte || []);
    const acceptedOfferId = richiesta?.offertaAccettataId || null;
    const acceptedOffer = (() => {
      const acceptedIndex = offerte.findIndex((offerta) => {
        const currentOfferId = offerta?.id || offerta?.sellerId || null;
        return currentOfferId && acceptedOfferId && currentOfferId === acceptedOfferId;
      });

      if (acceptedIndex >= 0) {
        return { ...offerte[acceptedIndex], displayLabel: `Proposta ${acceptedIndex + 1}` };
      }

      const fallbackIndex = offerte.findIndex((offerta) => ["SELEZIONATA", "ACCETTATA"].includes(offerta.stato));
      if (fallbackIndex >= 0) {
        return { ...offerte[fallbackIndex], displayLabel: `Proposta ${fallbackIndex + 1}` };
      }

      return null;
    })();
    const bestOffer = offerte[0] || null;
    const prodotti = Array.isArray(richiesta?.prodotti) ? richiesta.prodotti : [];
    const baseTotal = getBaseTotal(prodotti);
    const bestOfferSavings = bestOffer ? getOfferSavings(bestOffer, baseTotal) : { savings: 0, percent: 0 };
    const paymentState = richiesta?.pagamento?.stato || "NON_AVVIATO";
    const isPaymentPending = paymentState === "IN_ATTESA";
    const isPaid = paymentState === "PAGATO" || richiesta?.stato === "PAGATA";
    const isInProgress = richiesta?.stato === "IN_LAVORAZIONE";
    const isExpired = isRichiestaExpired(richiesta);
    const expiresAt = getRichiestaExpiresAt(richiesta);
    const fulfillmentPolicy = isPaid || isInProgress ? getOrderFulfillmentPolicy(richiesta) : null;
    const requestTitle = getRequestDisplayTitle(richiesta);
    const orderStatus = getCustomerOrderStatusPanel({
      richiesta,
      acceptedOffer,
      isInProgress,
      isPaid,
      isPaymentPending,
    });
    const summaryPanel = getCustomerSummaryPanel({
      acceptedOffer,
      isPaid,
      isPaymentPending,
      offerteCount: offerte.length,
      bestOfferTotal: formatPrice(bestOffer?.prezzoTotale || 0),
    });

    return (
      <div className="customer-requests-shell">
        <PageHero
          eyebrow="Dettaglio richiesta"
          title={requestTitle}
          description="Controlla i prodotti richiesti e confronta le offerte ricevute dal marketplace senza perdere il contesto della tua richiesta."
          linkBack={`${process.env.PUBLIC_URL}/richieste`}
          backLabel="Torna alle richieste"
        />

        {acceptError ? <div className="customer-payment-panel__error">{acceptError}</div> : null}
        {acceptSuccess ? <div className="customer-payment-panel__notice is-success">{acceptSuccess}</div> : null}
        {paymentError ? <div className="customer-payment-panel__error">{paymentError}</div> : null}
        {paymentNotice ? <div className="customer-payment-panel__notice">{paymentNotice}</div> : null}
        {isExpired ? (
          <div className="customer-payment-panel__error">
            Richiesta scaduta: la finestra di 24 ore e terminata
            {expiresAt ? ` (${formatDateTime(expiresAt.toISOString())}).` : "."}
          </div>
        ) : null}

        <StatusPanel
          className={`customer-order-status-card ${orderStatus.tone}`}
          tone={orderStatus.sharedTone}
          eyebrow={orderStatus.eyebrow}
          title={orderStatus.title}
          description={orderStatus.description}
          icon={orderStatus.icon}
          spinning={isPaymentPending}
        />

        <div className="customer-request-detail-grid">
          <SurfaceCard className="customer-request-detail-card customer-request-detail-card--main">
            <div className="customer-request-detail-card__top">
              <div>
                <span className="customer-request-detail-card__eyebrow">Riepilogo richiesta</span>
                <h2>{requestTitle}</h2>
                <p>
                  Creata il {formatDateTime(richiesta.createdAt || richiesta.updatedAt)}
                  {richiesta?.priorita ? ` � Priorita ${richiesta.priorita.toLowerCase()}` : ""}
                </p>
              </div>
              <div className="d-flex align-items-center">
                <StatusBadge>{isExpired ? "SCADUTA" : richiesta.stato || "INVIATA"}</StatusBadge>
              </div>
            </div>

            <div className="customer-request-detail-card__meta">
              <span>
                <i className="fa fa-shopping-basket" />
                {prodotti.length} prodotti
              </span>
              <span>
                <i className="fa fa-envelope-open-o" />
                {offerte.length} offerte ricevute
              </span>
            </div>

            {richiesta.noteUtente ? (
              <div className="customer-request-detail-note">
                <span>Note inserite</span>
                <p>{richiesta.noteUtente}</p>
              </div>
            ) : null}

            <div className="customer-request-detail-products">
              <h4>Prodotti richiesti</h4>
              <ul>
                {prodotti.map((prodotto, index) => (
                  <li key={prodotto.id || `${prodotto.nome}-${index}`}>
                    <div>
                      <strong>{prodotto.nome}</strong>
                      <span>
                        Quantita {prodotto.quantita || 0}
                        {prodotto.dosaggio ? ` � ${prodotto.dosaggio}` : ""}
                      </span>
                    </div>
                    <strong>
                      {formatPrice(Number(prodotto.prezzoIndicativo || 0) * Number(prodotto.quantita || 0))}
                    </strong>
                  </li>
                ))}
              </ul>
            </div>
          </SurfaceCard>

          <SurfaceCard className="customer-request-detail-card customer-request-detail-card--summary">
            <div className="customer-request-summary-metric">
              <span>Offerte ricevute</span>
              <strong>{offerte.length}</strong>
            </div>

            <DetailMetricCard
              label={acceptedOffer ? "Totale selezionato" : "Miglior totale"}
              value={
                acceptedOffer ? formatPrice(acceptedOffer.prezzoTotale) : bestOffer ? formatPrice(bestOffer.prezzoTotale) : "In attesa"
              }
            />

            {fulfillmentPolicy ? (
              <DetailMetricCard
                label="Tempo evasione"
                value={fulfillmentPolicy.shortLabel}
                helper={fulfillmentPolicy.helper}
              />
            ) : null}

            {!acceptedOffer && bestOffer ? (
              <div className="customer-request-summary-metric customer-request-summary-metric--discount">
                <span>Sconto migliore</span>
                <strong>{`${bestOfferSavings.percent}%`}</strong>
                <small>Risparmi {formatPrice(bestOfferSavings.savings)}</small>
              </div>
            ) : null}

            <div className="customer-request-summary-panel">
              <strong>{summaryPanel.title}</strong>
              <span>{summaryPanel.description}</span>
            </div>
          </SurfaceCard>
        </div>

        <section className="customer-offers-section customer-offers-section--full">
          <div className="customer-offers-section__header">
            <span className="marketplace-page-hero__eyebrow">Offerte ricevute</span>
            <h3>Confronta le proposte disponibili</h3>
          </div>

          {offerte.length > 0 ? (
            <div className="customer-offers-list">
              {offerte.map((offerta, index) =>
                this.renderOfferCard(offerta, index, baseTotal, acceptedOfferId, paymentState)
              )}
            </div>
          ) : (
            <EmptyStateCard
              icon="fa-hourglass-half"
              eyebrow="Nessuna offerta"
              title="Nessuna offerta disponibile"
              description="Appena arriveranno nuove proposte del marketplace, compariranno qui."
            />
          )}
        </section>
      </div>
    );
  }
}

export default DettaglioRichiestaCliente;








